import React, { useEffect, useState, useRef, useContext, useCallback, useMemo } from "react";
import axios from "../api";
import { UserContext } from "../context/UserContext";
import ConversationSidebar from "../components/ConversationSidebar.jsx";
import { buildConversationList, formatStamp } from "../utils/chatUtils";
import MessageStatusIcon from "../components/MessageStatusIcon";

// Module-level helpers for emoji + markdown and safe rendering
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const LINK_REGEX = /https?:\/\/[^\s)]+/g;
const EMOJI_MAP = { ':smile:':'😄', ':laughing:':'😆', ':wink:':'😉', ':heart:':'❤️', ':thumbsup:':'👍', ':fire:':'🔥', ':star:':'⭐', ':100:':'💯' };
const EMOJI_LIST = [
  '😀','😁','😂','🤣','😃','😄','😅','😉','😊','😍','😘','😜','🤪','🤗','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥','😭','😡','🤬','🤯','😴','🤤','🤓','😎','🤩','🥳','😇','🤠','😺','😸','😹','😻',
  '👍','👎','👌','🤌','✌️','🤞','🤟','🤘','👏','🙌','👐','🤝','🙏','💪','🫶',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','🔥','✨','⭐','🌟','💥','🎉','🎊','🎁','🎈',
  '🍕','🍔','🍟','🌮','🍣','🍪','🍰','☕','🍺','🍷',
  '⚽','🏀','🎮','🎧','📷','💡','📌','🔗','📎','📝','✅','❌','❗','❓','➡️','⬅️','⬆️','⬇️'
];
const EMOJI_INDEX = (() => {
  const m = new Map();
  EMOJI_LIST.forEach(ch => {
    const kw = [];
    if ('😀😁😂🤣😃😄😅😉😊😍😘😜🤪🤗😎🤩🥳😇'.includes(ch)) kw.push('smile','happy','face');
    if ('😭😡🤬🤯😣😥'.includes(ch)) kw.push('sad','angry','cry');
    if ('👍👎👌✌️🤞🤟🤘👏🙌👐🤝🙏💪🫶'.includes(ch)) kw.push('hand','gesture');
    if ('❤️🧡💛💚💙💜🖤🤍🤎💔'.includes(ch)) kw.push('heart','love');
    if ('🔥✨⭐🌟💥'.includes(ch)) kw.push('star','fire','spark');
    if ('🎉🎊🎁🎈'.includes(ch)) kw.push('party','gift','balloon');
    if ('🍕🍔🍟🌮🍣🍪🍰☕🍺🍷'.includes(ch)) kw.push('food','drink');
    if ('⚽🏀🎮🎧'.includes(ch)) kw.push('sport','game','music');
    if ('📷💡📌🔗📎📝'.includes(ch)) kw.push('tool','idea','link','note');
    if ('✅❌❗❓➡️⬅️⬆️⬇️'.includes(ch)) kw.push('mark','arrow','yes','no');
    m.set(ch, kw);
  });
  return m;
})();

function applyEmojiShortcodes(text) {
  if (!text) return text;
  return text.replace(/:[a-z0-9_+-]+:/gi, m => EMOJI_MAP[m] || m);
}
function renderChatMarkdown(text) {
  const withEmoji = applyEmojiShortcodes(text);
  const html = marked.parse(withEmoji || '', { breaks: true });
  // Hint browser to load/decode images more smoothly; helps reduce layout/scrollbar jitter.
  const htmlWithImgAttrs = html
    .replace(/<img(?![^>]*\bdecoding=)/g, '<img decoding="async"')
    .replace(/<img(?![^>]*\bloading=)/g, '<img loading="lazy"');
  return DOMPurify.sanitize(htmlWithImgAttrs);
}

async function compressImageUnder(bytesTarget, file, maxDim = 1920) {
  if (!file || !(file instanceof Blob)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, outW, outH);
    let q = 0.9;
    for (let i = 0; i < 6; i++) {
      const blob = await new Promise(res => canvas.toBlob(res, 'image/webp', q));
      if (!blob) break;
      if (blob.size <= bytesTarget || q <= 0.5) {
        const out = new File([blob], (file.name || 'image') + '.webp', { type: 'image/webp' });
        return out;
      }
      q -= 0.1;
    }
    const blob = await new Promise(res => canvas.toBlob(res, 'image/webp', 0.5));
    if (blob) return new File([blob], (file.name || 'image') + '.webp', { type: 'image/webp' });
    return file;
  } catch {
    return file;
  } finally {
    try { URL.revokeObjectURL(url); } catch {}
  }
}

function EmojiGrid({ filter, onPick }) {
  const f = (filter || '').toLowerCase().trim();
  const items = useMemo(() => {
    if (!f) return EMOJI_LIST;
    return EMOJI_LIST.filter(ch => {
      const kws = EMOJI_INDEX.get(ch) || [];
      return kws.some(k => k.includes(f));
    });
  }, [f]);
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(8, 1fr)', gap:6 }}>
      {items.map((ch, idx) => (
        <button key={idx} type="button" onClick={() => onPick(ch)}
          style={{ background:'#2a2a2a', border:'1px solid #3a3a3a', borderRadius:8, padding:'6px 0', cursor:'pointer', fontSize:20 }}
          aria-label={`Emoji ${idx}`}
        >{ch}</button>
      ))}
      {items.length === 0 && <div style={{ gridColumn:'1 / -1', fontSize:12, opacity:.6, padding:'6px 2px' }}>Keine Treffer</div>}
    </div>
  );
}

export default function Chat() {
  const { user, subscribeWS } = useContext(UserContext);

  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiFilter, setEmojiFilter] = useState('');
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const [uploads, setUploads] = useState([]); // {id, localUrl, name, size, progress, status, result, errorMessage}
  const [attachPosition, setAttachPosition] = useState('before');
  const [lightbox, setLightbox] = useState({ open:false, url:'' });

  // System-Meta für die Sortierung in der Sidebar
  const [systemMeta, setSystemMeta] = useState({
    lastMessageAt: null,
    lastMessage: "",
    unread: false,
  });

  const chatMessagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const nearBottomRef = useRef(true);
  const scrollToBottomRafRef = useRef(null);
  const scrollListenerRafRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewWhileAway, setHasNewWhileAway] = useState(false);
  const lastMessageCountRef = useRef(0);
  const lastActiveUserIdRef = useRef(null);
  const activeUserRef = useRef(null);
  const userRef = useRef(null);
  useEffect(() => () => {
    try { uploads.forEach(u => { if (u.localUrl) URL.revokeObjectURL(u.localUrl); }); } catch {}
  }, []);

  const scrollToBottom = useCallback((smooth = false) => {
    try {
      if (!messagesEndRef.current) return;
      if (scrollToBottomRafRef.current) cancelAnimationFrame(scrollToBottomRafRef.current);
      scrollToBottomRafRef.current = requestAnimationFrame(() => {
        scrollToBottomRafRef.current = null;
        try {
          messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
        } catch {}
      });
    } catch {}
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (scrollToBottomRafRef.current) cancelAnimationFrame(scrollToBottomRafRef.current);
        if (scrollListenerRafRef.current) cancelAnimationFrame(scrollListenerRafRef.current);
      } catch {}
    };
  }, []);

  // immer aktuelle Werte im Ref halten (gegen stale closures)
  useEffect(() => {
    activeUserRef.current = activeUser;
  }, [activeUser]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Track whether user is near the bottom (enables auto-scroll + jump button)
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const onScroll = () => {
      if (scrollListenerRafRef.current) return;
      scrollListenerRafRef.current = requestAnimationFrame(() => {
        scrollListenerRafRef.current = null;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const isNear = distanceFromBottom < 140;
        nearBottomRef.current = isNear;
        setIsNearBottom(prev => (prev === isNear ? prev : isNear));
        if (isNear) setHasNewWhileAway(false);
      });
    };
    onScroll();
    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [activeUser]);

  // Auto-scroll only when user is at/near bottom; otherwise show "Neue Nachrichten" indicator.
  useEffect(() => {
    const activeId = activeUser?.userId ?? null;
    const activeChanged = lastActiveUserIdRef.current !== activeId;
    lastActiveUserIdRef.current = activeId;

    const prevCount = lastMessageCountRef.current;
    const nextCount = messages.length;
    lastMessageCountRef.current = nextCount;

    if (!activeId) return;

    if (activeChanged) {
      setHasNewWhileAway(false);
      scrollToBottom(false);
      return;
    }

    if (nextCount > prevCount) {
      if (nearBottomRef.current) {
        scrollToBottom(false);
      } else {
        setHasNewWhileAway(true);
      }
    }
  }, [messages.length, activeUser?.userId, scrollToBottom]);

  // Delegate image link clicks to lightbox
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const handler = (e) => {
      const a = e.target?.closest?.('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href) return;
      if (href.includes('/api/uploads/chat-images/') || href.startsWith('/uploads/chat-images/')) {
        e.preventDefault();
        setLightbox({ open:true, url: href });
      }
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [chatMessagesRef]);

  // System-Meta laden (nur jüngste Info für Sidebar)
  const fetchSystemMeta = useCallback(async () => {
    try {
      // Schnellweg, falls dein Backend limit unterstützt
      const r = await axios.get("/rundmail/intern", { params: { limit: 1 } });
      const items = r.data || [];
      const latest = Array.isArray(items) && items.length ? items[0] : null;
      if (latest) {
        setSystemMeta({
          lastMessageAt:
            latest.sentAt || latest.createdAt || latest.date || null,
          lastMessage: latest.subject || latest.body || "",
          unread: false,
        });
        return;
      }
    } catch {
      // ignoriert und versucht Fallback
    }

    // Fallback: gesamten Feed laden und lokal jüngstes ermitteln
    try {
      const res = await axios.get("/rundmail/intern");
      const sorted = (res.data || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.sentAt).valueOf() - new Date(a.sentAt).valueOf()
        );
      const latest = sorted[0];
      if (latest) {
        setSystemMeta({
          lastMessageAt:
            latest.sentAt || latest.createdAt || latest.date || null,
          lastMessage: latest.subject || latest.body || "",
          unread: false,
        });
      }
    } catch {
      // still no system meta; keep default
    }
  }, []);

  // Initialdaten laden
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function bootstrap() {
      try {
        const [convRes, usersRes] = await Promise.all([
          axios.get("/messages/conversations", { signal: controller.signal }),
          axios.get("/users/list", { signal: controller.signal }),
        ]);
        if (!alive) return;
        setConversations(convRes.data || []);
        setAllUsers(usersRes.data?.items || []);
        // System-Meta zusätzlich holen
        fetchSystemMeta();
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") {
          console.error("Bootstrap-Fehler:", err);
        }
      }
    }
    bootstrap();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [fetchSystemMeta]);

  // WebSocket subscription for live messages
  useEffect(() => {
    if (!subscribeWS) return;
    // Handler für eingehende WebSocket-Nachrichten
    const unsubscribe = subscribeWS(async (data) => {
      if (data?.type !== "new_message" || !data.message) return;

      const me = userRef.current;
      const currentActive = activeUserRef.current;
      if (!me) return;

      // 1) Systemnachrichten speziell behandeln
      if (data.message.type === 'system') {
        const myId = Number(me.id);
        const recipientId = Number(data.message.recipientId);
        if (recipientId !== myId) return;

        // System-Meta direkt aktualisieren (für Sidebar-Eintrag)
        const lastAt = data.message.sentAt || data.message.createdAt || new Date().toISOString();
        const isActiveSystem = currentActive?.userId === 'system';
        setSystemMeta(() => ({
          lastMessageAt: lastAt,
          lastMessage: data.message.subject || data.message.body || '',
          // Markiere als „neu“, wenn System-Konversation nicht geöffnet ist
          unread: !isActiveSystem,
        }));

        // Wenn System-Konversation offen ist, Nachricht anhängen
        if (currentActive?.userId === 'system') {
          setMessages((prev) => {
            const next = [...prev, data.message];
            next.sort((a, b) => {
              const ta = new Date(a.sentAt || a.createdAt).valueOf();
              const tb = new Date(b.sentAt || b.createdAt).valueOf();
              return ta - tb;
            });
            return next;
          });
          // Sofort als gelesen markieren, wenn die System-Konversation geöffnet ist
          if (!data.message.read) {
            axios.patch(`/messages/${data.message.id}/read`).catch(() => {});
          }
        }
        return;
      }

      // 2) Normale private Nachrichten (bestehende Logik)
      // Konversationsliste in Echtzeit aktualisieren (betrifft nur echte Konversationen)
      try {
        const convRes = await axios.get("/messages/conversations");
        setConversations(convRes.data || []);
      } catch {
        // noop
      }

      setMessages((prev) => {
        const me2 = userRef.current;
        const currentActive2 = activeUserRef.current;
        if (!currentActive2 || !me2) return prev;

        const senderId = Number(data.message.senderId);
        const recipientId = Number(data.message.recipientId);
        const activeId = Number(currentActive2.userId);
        const myId = Number(me2.id);

        const relevant =
          (senderId === activeId && recipientId === myId) ||
          (senderId === myId && recipientId === activeId);
        if (!relevant) return prev;

        // Wenn die neue Nachricht an mich geht und die Konversation offen ist, sofort als gelesen markieren
        if (recipientId === myId && senderId === activeId && !data.message.read) {
          axios.patch(`/messages/${data.message.id}/read`).catch(() => {});
        }

        // Wenn die Nachricht als gelesen markiert wurde (z. B. durch anderen Client), aktualisiere Liste
        if (data.message.read) {
          // Hole aktuelle Nachrichten für die Konversation
          axios
            .get(`/messages/conversation/${activeId}`)
            .then((res) => {
              const sorted = (res.data || [])
                .slice()
                .sort(
                  (a, b) =>
                    new Date(a.sentAt).valueOf() -
                    new Date(b.sentAt).valueOf()
                );
              setMessages(sorted);
            })
            .catch(() => {});
          // Aktualisiere die Konversationsübersicht
          axios
            .get("/messages/conversations")
            .then((res) => {
              setConversations(res.data || []);
            })
            .catch(() => {});
        }

        const next = [...prev, data.message];
        next.sort(
          (a, b) => new Date(a.sentAt).valueOf() - new Date(b.sentAt).valueOf()
        );
        return next;
      });
    });
    return unsubscribe;
  }, [subscribeWS]);

  const openConversation = useCallback(
    (c) => {
      setActiveUser(c);
      setLoading(true);

      if (c.userId === "system") {
        axios
          .get("/rundmail/intern")
          .then((res) => {
            const sorted = (res.data || [])
              .slice()
              .sort(
                (a, b) =>
                  new Date(a.sentAt).valueOf() - new Date(b.sentAt).valueOf()
              );
            setMessages(sorted);

            // System-Meta aktualisieren
            const last = sorted[sorted.length - 1];
            if (last) {
              setSystemMeta({
                lastMessageAt:
                  last.sentAt || last.createdAt || last.date || null,
                lastMessage: last.subject || last.body || "",
                unread: false,
              });
            }

            // Alle ungelesenen Systemnachrichten als gelesen markieren
            try {
              const myId = Number(user?.id);
              const unread = sorted.filter((msg) => !msg.read && Number(msg.recipientId) === myId);
              for (const msg of unread) {
                // fire-and-forget, nicht blockieren
                axios.patch(`/messages/${msg.id}/read`).catch(() => {});
              }
            } catch {}

            setTimeout(() => {
              scrollToBottom(true);
            }, 50);
          })
          .catch((e) => {
            console.error("Systemnachrichten laden fehlgeschlagen:", e);
          })
          .finally(() => setLoading(false));
      } else {
        axios
          .get(`/messages/conversation/${c.userId}`)
          .then(async (res) => {
            const sorted = (res.data || [])
              .slice()
              .sort(
                (a, b) =>
                  new Date(a.sentAt).valueOf() - new Date(b.sentAt).valueOf()
              );
            setMessages(sorted);

            setTimeout(() => {
              scrollToBottom(true);
            }, 50);

            const myId = Number(user?.id);
            const unread = sorted.filter(
              (msg) => !msg.read && Number(msg.recipientId) === myId
            );
            for (const msg of unread) {
              try {
                await axios.patch(`/messages/${msg.id}/read`);
              } catch {}
            }

            try {
              const convRes = await axios.get("/messages/conversations");
              setConversations(convRes.data || []);
            } catch {}
          })
          .catch((e) => console.error("Konversation laden fehlgeschlagen:", e))
          .finally(() => setLoading(false));
      }
    },
    [user]
  );

  const sendMessage = useCallback(
    async (e) => {
      e.preventDefault();
      const body = newMessage.trim();
      if (!activeUser) return;
      // auto-attachments
      const ready = uploads.filter(u => u.status === 'done' && u.result);
      // allow sending attachments-only (no typed text)
      if (!body && ready.length === 0) return;
      const readyBefore = ready.filter(u => (u.position || attachPosition || 'before') === 'before');
      const readyAfter  = ready.filter(u => (u.position || attachPosition || 'before') === 'after');
      const buildMd = (arr) => arr.map(u => {
        const thumb = u.result?.thumbApi || u.result?.thumb || u.result?.displayApi || u.result?.display || u.result?.originalApi || u.result?.original;
        const full = u.result?.displayApi || u.result?.display || u.result?.originalApi || u.result?.original || thumb;
        return `[![](${thumb})](${full})`;
      }).join('\n\n');
      const attachMdBefore = buildMd(readyBefore);
      const attachMdAfter  = buildMd(readyAfter);
      const parts = [];
      if (attachMdBefore) parts.push(attachMdBefore);
      if (body) parts.push(body);
      if (attachMdAfter) parts.push(attachMdAfter);
      const finalBody = parts.join('\n\n');

      setNewMessage("");
      try {
        await axios.post("/messages/send", {
          recipientId: activeUser.userId,
          body: finalBody,
        });

        // Konversationsliste direkt neu laden
        try {
          const convRes = await axios.get("/messages/conversations");
          setConversations(convRes.data || []);
        } catch {}

        // sanft nachladen (oder: optimistisch pushen)
        const res = await axios.get(
          `/messages/conversation/${activeUser.userId}`
        );
        const sorted = (res.data || [])
          .slice()
          .sort(
            (a, b) =>
              new Date(a.sentAt).valueOf() - new Date(b.sentAt).valueOf()
          );
        setMessages(sorted);
        // clear pending uploads
        setUploads(list => { list.forEach(u => { if (u.localUrl) { try { URL.revokeObjectURL(u.localUrl); } catch {} } }); return []; });
      } catch (e2) {
        console.error("Senden fehlgeschlagen:", e2);
        // Eingabe wiederherstellen, damit nichts „verschwindet“
        setNewMessage(body);
      }
    },
    [newMessage, activeUser, uploads, attachPosition]
  );

  // Neue Konversation aus der Sidebar starten
  const startNewConversation = useCallback(
    async (uid) => {
      const u = allUsers.find((x) => Number(x.id) === Number(uid));
      if (!u) return;

      const target = {
        userId: u.id,
        username: u.username,
        displayName: u.displayName,
      };
      setActiveUser(target);

      try {
        const res = await axios.get(`/messages/conversation/${u.id}`);
        setMessages(res.data || []);
      } catch {}

      try {
        const convRes = await axios.get("/messages/conversations");
        setConversations(convRes.data || []);
      } catch {}
    },
    [allUsers]
  );

  // Sidebar-Items aufbauen (inkl. System)
  const sidebarItems = useMemo(() => {
    return buildConversationList(conversations, user, systemMeta);
  }, [conversations, user, systemMeta]);

  // Emoji insertion
  function insertEmoji(ch) {
    const el = inputRef.current;
    const s = newMessage || '';
    if (!el) {
      setNewMessage(s + ch);
      setEmojiOpen(false);
      return;
    }
    try {
      const start = el.selectionStart ?? s.length;
      const end = el.selectionEnd ?? s.length;
      const next = s.slice(0, start) + ch + s.slice(end);
      setNewMessage(next);
      setEmojiOpen(false);
      setTimeout(() => { try { el.focus(); el.setSelectionRange(start + ch.length, start + ch.length); } catch {} }, 10);
    } catch {
      setNewMessage(s + ch);
      setEmojiOpen(false);
    }
  }

  async function onPickImage(file) {
    if (!file) return;
    if (file.size > 2*1024*1024) { alert('Bild zu groß (max. 2MB)'); return; }
    const id = Math.random().toString(36).slice(2,9);
    const localUrl = URL.createObjectURL(file);
    setUploads(u => [...u, { id, localUrl, name: file.name, size: file.size, progress: 0, status: 'uploading', position: attachPosition }]);
    let currentFile = file;
    const postOnce = async () => {
      const fd = new FormData();
      fd.append('image', currentFile);
      const { data } = await axios.post('/community-chat/upload-image', fd, {
        onUploadProgress: (evt) => {
          if (!evt || !evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploads(list => list.map(x => x.id === id ? { ...x, progress: p } : x));
        }
      });
      return data;
    };
    try {
      const data = await postOnce();
      setUploads(list => list.map(x => x.id === id ? { ...x, status: 'done', result: data } : x));
    } catch (e) {
      if (e?.response?.status === 413) {
        setUploads(list => list.map(x => x.id === id ? { ...x, errorMessage: 'Proxy-Limit 1MB – Bild wird komprimiert…' } : x));
        try {
          currentFile = await compressImageUnder(1000*1000, file, 1920);
          const data = await postOnce();
          setUploads(list => list.map(x => x.id === id ? { ...x, status: 'done', result: data, errorMessage: undefined } : x));
        } catch (e2) {
          const msg = e2?.response?.data?.error || e2?.message || 'Upload fehlgeschlagen (nach Komprimierung)';
          setUploads(list => list.map(x => x.id === id ? { ...x, status: 'error', errorMessage: msg } : x));
        }
      } else {
        const msg = e?.response?.data?.error || e?.message || 'Upload fehlgeschlagen';
        setUploads(list => list.map(x => x.id === id ? { ...x, status: 'error', errorMessage: msg } : x));
      }
    } finally {
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch {}
    }
  }

  function removeUpload(id) {
    setUploads(list => {
      const target = list.find(x => x.id === id);
      if (target?.localUrl) {
        try { URL.revokeObjectURL(target.localUrl); } catch {}
      }
      return list.filter(x => x.id !== id);
    });
  }

  function setUploadPosition(id, pos) {
    setUploads(list => list.map(x => x.id === id ? { ...x, position: pos } : x));
  }

  return (
    <>
    <div
      style={{
        width: "100vw",
        minHeight: "100vh",
        background: "#181818",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          maxWidth: 900,
          width: "100%",
          margin: "40px auto",
          background: "#222",
          borderRadius: 16,
          boxShadow: "0 2px 16px #0002",
          padding: 0,
        }}
      >
        <div
          className="chat-container"
          style={{ display: "flex", flexDirection: "row", minHeight: 500 }}
        >
          {/* Sidebar ersetzt durch eigene Komponente */}
          <ConversationSidebar
            items={sidebarItems}
            user={user}
            activeUser={activeUser}
            onOpen={openConversation}
            allUsers={allUsers}
            onStartNewConversation={startNewConversation}
          />

          <div className="chat-main" style={{ flex: 1, padding: 16, minWidth: 0 }}>
            {activeUser ? (
              <>
                <div className="chat-header">
                  <h3>{activeUser.displayName || activeUser.username}</h3>
                </div>

                <div style={{ position: 'relative' }}>
                  <div
                    className="chat-messages"
                    ref={chatMessagesRef}
                    style={{
                      maxHeight: "60vh",
                      minHeight: "200px",
                      overflowY: "auto",
                      paddingRight: 8,
                      scrollbarGutter: 'stable',
                      overflowAnchor: 'none',
                    }}
                  >
                    {loading ? (
                      <div>Lade Nachrichten...</div>
                    ) : activeUser.userId === "system" ? (
                      // Systemnachrichten-Konversation
                      messages.map((msg) => (
                      <div
                        key={String(msg.id)}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            maxWidth: "70%",
                            background: "#2a3a4d",
                            color: "#fff",
                            borderRadius: 18,
                            boxShadow: "0 2px 8px #0002",
                            padding: "10px 16px",
                            position: "relative",
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.subject && (
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 15,
                                marginBottom: 4,
                              }}
                            >
                              {msg.subject}
                            </div>
                          )}
                          <div className="chat-md" style={{ fontSize: 16, marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: renderChatMarkdown(msg.body) }} />
                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.7,
                              marginTop: 2,
                              textAlign: "right",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span>{formatStamp(msg.sentAt)}</span>
                            <span>· System</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Normale Konversation
                    Array.from(
                      new Map(messages.map((m) => [m.id, m])).values()
                    ).map((msg) => {
                      const isSelf = Number(msg.senderId) === Number(user?.id);
                      return (
                        <div
                          key={String(msg.id)}
                          style={{
                            display: "flex",
                            justifyContent: isSelf ? "flex-end" : "flex-start",
                            marginBottom: 12,
                          }}
                        >
                          <div
                            style={{
                              maxWidth: "70%",
                              background: isSelf ? "#4f8cff" : "#333",
                              color: isSelf ? "#fff" : "#eee",
                              borderRadius: 18,
                              boxShadow: "0 2px 8px #0002",
                              padding: "10px 16px",
                              position: "relative",
                              wordBreak: "break-word",
                            }}
                          >
                            <div className="chat-md" style={{ fontSize: 16, marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: renderChatMarkdown(msg.body) }} />
                            <div
                              style={{
                                fontSize: 12,
                                opacity: 0.7,
                                marginTop: 2,
                                textAlign: "right",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <span>{formatStamp(msg.sentAt)}</span>
                              {/* Haken nur bei eigenen Nachrichten anzeigen */}
                              {Number(msg.senderId) === Number(user?.id) && (
                                /*<span>
                                  {/* 1 grauer Haken: gesendet, 2 graue: zugestellt, 2 grüne: gelesen /}
                                  {!msg.delivered && !msg.read && (
                                    <span
                                      title="Gesendet"
                                      style={{ color: "#aaa", fontSize: 14 }}
                                    >
                                      ✔
                                    </span>
                                  )}
                                  {msg.delivered && !msg.read && (
                                    <span
                                      title="Zugestellt"
                                      style={{ color: "#aaa", fontSize: 14 }}
                                    >
                                      ✔✔
                                    </span>
                                  )}
                                  {msg.read && (
                                    <span
                                      title="Gelesen"
                                      style={{ color: "#2ecc40", fontSize: 14 }}
                                    >
                                      ✔✔
                                    </span>
                                  )}
                                </span>
                                */
                                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <MessageStatusIcon delivered={!!msg.delivered} read={!!msg.read} />
                                </span>
                              )}
                              <span>
                                · {msg.senderDisplayName || msg.senderUsername}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  ))}
                    <div ref={messagesEndRef} />
                  </div>
                  {activeUser && !isNearBottom && (
                    <button
                      type="button"
                      onClick={() => {
                        setHasNewWhileAway(false);
                        scrollToBottom(true);
                      }}
                      style={{
                        position: 'absolute',
                        left: 16,
                        bottom: 16,
                        background: '#32CD89',
                        color: '#032d1e',
                        fontWeight: 800,
                        border: '1px solid #32CD89',
                        padding: '10px 14px',
                        borderRadius: 999,
                        cursor: 'pointer',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
                        zIndex: 20,
                        maxWidth: 'calc(100% - 32px)'
                      }}
                      aria-label="Zum Ende springen"
                      title="Zum Ende"
                    >
                      {hasNewWhileAway ? 'Neue Nachrichten – Zum Ende' : 'Zum Ende'}
                    </button>
                  )}
                </div>

                {/* Antwortfeld nur bei normalen Konversationen anzeigen */}
                {activeUser.userId !== "system" && (
                  <>
                    <form className="chat-input" onSubmit={sendMessage} style={{ display:'flex', gap:10, alignItems:'center', paddingTop:8, position:'relative' }}>
                      <button type="button" onClick={() => setEmojiOpen(o => !o)} aria-label="Emoji auswählen" title="Emoji auswählen" style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'0 10px', cursor:'pointer' }}>
                        <span style={{ fontSize:20, lineHeight:'40px', display:'inline-block' }}>😊</span>
                      </button>
                      <button type="button" onClick={() => fileInputRef.current?.click()} aria-label="Datei anhängen" title="Datei anhängen" style={{ background:'#2b2b2b', color:'#fff', border:'1px solid #444', borderRadius:10, padding:'0 10px', cursor:'pointer' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display:'block' }}>
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.2a2 2 0 1 1-2.83-2.83l8.49-8.49" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" ref={fileInputRef} style={{ display:'none' }} onChange={(e)=> onPickImage(e.target.files?.[0])} />
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Nachricht schreiben..."
                        autoComplete="off"
                        ref={inputRef}
                        style={{ flex:1 }}
                      />
                      <button type="submit" disabled={(!!activeUser ? false : true) || (!newMessage.trim() && uploads.filter(u=>u.status==='done' && u.result).length===0)}>Senden</button>

                      {emojiOpen && (
                        <div style={{ position:'absolute', bottom:58, left:0, zIndex:50, background:'#1c1c1c', border:'1px solid #333', borderRadius:10, padding:10, width:320, maxHeight:320, overflow:'hidden', boxShadow:'0 6px 24px rgba(0,0,0,0.4)' }} role="dialog" aria-label="Emoji-Auswahl">
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                            <input type="text" placeholder="Emoji suchen…" value={emojiFilter} onChange={(e)=> setEmojiFilter(e.target.value)} style={{ flex:1, background:'#111', color:'#fff', border:'1px solid #333', borderRadius:8, padding:'8px 10px', fontSize:13 }} />
                            <button type="button" onClick={()=> setEmojiOpen(false)} style={{ background:'#333', color:'#fff', border:'1px solid #444', borderRadius:8, padding:'8px 10px', cursor:'pointer', fontSize:12 }}>Schließen</button>
                          </div>
                          <div style={{ overflowY:'auto', maxHeight:260 }}>
                            <EmojiGrid filter={emojiFilter} onPick={(ch)=> insertEmoji(ch)} />
                          </div>
                        </div>
                      )}
                    </form>
                    {/* Pending uploads preview + attach position */}
                    {uploads.length > 0 && (
                      <div style={{ padding:'6px 0 10px' }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
                          {uploads.map(u => (
                            <div key={u.id} style={{ background:'#1a1a1a', border:'1px solid #333', borderRadius:10, padding:10, width:220 }}>
                              <div style={{ width:'100%', height:120, background:'#111', borderRadius:8, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                {u.localUrl ? <img src={u.localUrl} alt={u.name} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} /> : <div style={{ color:'#888', fontSize:12 }}>Vorschau</div>}
                              </div>
                              <div style={{ fontSize:12, marginTop:8, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.name}</div>
                              <div style={{ fontSize:11, opacity:.7 }}>{Math.round(u.size/1024)} KB</div>
                              <div style={{ marginTop:8, display:'flex', gap:8, alignItems:'center' }}>
                                <span style={{ fontSize:12, opacity:.75 }}>Relativ zum Text:</span>
                                <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                                  <input type="radio" name={`pos-${u.id}`} checked={(u.position || 'before')==='before'} onChange={()=> setUploadPosition(u.id, 'before')} /> darüber <span style={{opacity:.6}}>(Text unter dem Bild)</span>
                                </label>
                                <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                                  <input type="radio" name={`pos-${u.id}`} checked={(u.position || 'before')==='after'} onChange={()=> setUploadPosition(u.id, 'after')} /> darunter <span style={{opacity:.6}}>(Text über dem Bild)</span>
                                </label>
                              </div>
                              <div style={{ marginTop:8, height:6, background:'#2d2d2d', borderRadius:4, overflow:'hidden' }}>
                                <div style={{ width:`${u.progress|| (u.status==='done'?100:0)}%`, height:'100%', background: u.status==='error' ? '#ff6b6b' : '#32CD89' }} />
                              </div>
                              <div style={{ marginTop:8, display:'flex', gap:8, alignItems:'center' }}>
                                <span style={{ fontSize:12, color: u.status==='done' ? '#9fe7b2' : (u.status==='error' ? '#ff9a9a' : '#bbb') }}>
                                  {u.status==='done' ? 'Wird angehängt' : (u.status==='error' ? (u.errorMessage || 'Fehler beim Upload') : 'Lade…')}
                                </span>
                                <button type="button" onClick={()=>removeUpload(u.id)} style={{ marginLeft:'auto', background:'#3a2a2a', color:'#ffb3b3', border:'1px solid #5a3a3a', borderRadius:8, padding:'6px 8px', fontSize:12, cursor:'pointer' }}>Entfernen</button>
                              </div>
                              {u.status==='error' && u.errorMessage && (
                                <div style={{ marginTop:6, fontSize:11, color:'#ff9a9a' }}>{u.errorMessage}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="chat-placeholder">Wähle eine Konversation aus.</div>
            )}
          </div>
        </div>
      </div>
    </div>
    {lightbox.open && (
      <div onClick={() => setLightbox({ open:false, url:'' })} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
        <div onClick={(e)=> e.stopPropagation()} style={{ maxWidth:'92vw', maxHeight:'88vh', borderRadius:10, overflow:'hidden', border:'1px solid #444', background:'#111', padding:10 }}>
          <img src={lightbox.url} alt="Bild" style={{ maxWidth:'90vw', maxHeight:'85vh', display:'block' }} />
        </div>
        <button onClick={() => setLightbox({ open:false, url:'' })} style={{ position:'fixed', top:20, right:20, background:'#333', color:'#fff', border:'1px solid #444', borderRadius:8, padding:'8px 12px', cursor:'pointer' }}>Schließen</button>
      </div>
    )}
    </>
  );
}

// Ensure images in chat markdown render as block elements, so text doesn't sit beside them
if (typeof document !== 'undefined' && !document.getElementById('chat-md-style')) {
  const style = document.createElement('style');
  style.id = 'chat-md-style';
  style.textContent = `.chat-md img{display:block;max-width:100%;width:min(100%,520px);height:auto;min-height:140px;max-height:420px;object-fit:contain;margin:8px 0;border-radius:10px;background:rgba(255,255,255,0.04);} .chat-md p{margin:8px 0;} .chat-md a{display:inline-block;}`;
  document.head.appendChild(style);
}
